/**
 * Formatting + day-grouping helpers for the candidate booking page.
 *
 * Every slot the proxy returns is a UTC instant; the candidate reasons in Berlin
 * time, so ALL rendering goes through these Berlin-pinned formatters. Grouping
 * by a locally-derived date would put an 00:30 Berlin slot on the wrong day.
 */
import type { BookingSlot } from '../../services/interviewBookingApi';

export const TZ = 'Europe/Berlin';

// Module-level: Intl construction is comparatively expensive and these never vary.
const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const weekdayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'short' });
const dayNumFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: 'numeric' });
const monthFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, month: 'short' });

export const timeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
export const longDayFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** Berlin calendar date of an instant, as YYYY-MM-DD (the grouping key). */
export const dayKey = (iso: string) => dayKeyFmt.format(new Date(iso));

export const fmtTime = (iso: string) => timeFmt.format(new Date(iso));
export const fmtLongDay = (iso: string) => longDayFmt.format(new Date(iso));
export const slotRange = (slot: BookingSlot) => `${fmtTime(slot.start)} – ${fmtTime(slot.end)}`;

export interface BookingDay {
  key: string;
  weekday: string;
  dayNum: string;
  month: string;
  slots: BookingSlot[];
}

/** Group slots into Berlin calendar days, preserving chronological order. */
export function groupByDay(slots: BookingSlot[]): BookingDay[] {
  const days: BookingDay[] = [];
  const byKey = new Map<string, BookingDay>();
  for (const slot of slots) {
    const key = dayKey(slot.start);
    let day = byKey.get(key);
    if (!day) {
      const d = new Date(slot.start);
      day = {
        key,
        weekday: weekdayFmt.format(d),
        dayNum: dayNumFmt.format(d),
        month: monthFmt.format(d),
        slots: [],
      };
      byKey.set(key, day);
      days.push(day);
    }
    day.slots.push(slot);
  }
  return days;
}

/** Same rule as the proxy's isValidEmail — one plausible address, nothing else. */
export const emailLooksValid = (value: string) =>
  /^[^\s@,;]+@[^\s@,;.]+\.[^\s@,;]+$/.test(value.trim());

/**
 * First name for the greeting. Falls back to null rather than a placeholder so
 * the page can drop the greeting entirely instead of saying "Hi there".
 */
export function firstName(fullName?: string | null): string | null {
  const first = `${fullName || ''}`.trim().split(/\s+/)[0];
  return first || null;
}

/**
 * linkedin.com/in/<handle>, loosely — the proxy's `normalizeLinkedInUrl` is the real check. This
 * only keeps the button from inviting a submit the proxy will refuse.
 */
export const linkedinUrlLooksValid = (v: string) =>
  /^(https?:\/\/)?([a-z]{2,3}\.)?(www\.)?linkedin\.com\/in\/[^/\s?#]+\/?([?#].*)?$/i.test(v.trim());
